import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { untrackUser } from '@/analytics';
import { LocalStorageKeys } from '@/constants/LocalStorage';
import useIdentifyUser from '@/hooks/useIdentifyUser';
import { meQueryOptions } from '@/queries/meQuery/meQuery';
import { useAuthToken } from '@/store/authTokenStore';

const useHandleUserTracking = () => {
  const lastIdentifiedUserId = useRef<string | null>(null);
  const authToken = useAuthToken();
  const identifyUser = useIdentifyUser();
  /**
   * The query is disabled to avoid fetching /me from this hook. In the UK application flow,
   * the auth token is received before the user reaches a RequireAuth-protected route, so an
   * active fetch here would fire prematurely. Instead, we read from the cache once RequireAuth
   * (or another authenticated route) has populated it.
   */
  const { data: me } = useQuery({ ...meQueryOptions(), enabled: false });

  useEffect(
    function track() {
      if (!authToken || !me?.id || lastIdentifiedUserId.current === me.id) {
        return;
      }

      lastIdentifiedUserId.current = me.id;

      /**
       * To avoid increasing our Monthly Active Users (MAU) count in DevCycle, we store the user
       * id in local storage and initialize DevCycle with it.
       *
       * For the same reason we do not reset DevCycle's state when the user logs out.
       */
      localStorage.setItem(LocalStorageKeys.USER_ID, me.id);
      void identifyUser(me.id);
    },
    [me?.id, identifyUser, authToken],
  );

  useEffect(
    function untrack() {
      if (!!authToken || lastIdentifiedUserId.current === null) {
        return;
      }

      lastIdentifiedUserId.current = null;
      /**
       * We purposefully don't call DevCycle's reset function here because it would count it
       * as a separate Monthly Active User (MAU), which is not accurate.
       */
      untrackUser();
    },
    [authToken],
  );
};

export default useHandleUserTracking;
